import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios, {
  apiBaseUrl,
  clearStoredSessionToken,
  getStoredSessionToken,
  setStoredSessionToken,
} from '../api/httpClient';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
  roleLabel: string;
  permissions: string[];
  isActive: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeMicrosoftLogin: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface LoginResponse {
  token: string;
  user: AuthUser;
}

interface MeResponse {
  user: AuthUser;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getStoredSessionToken();

    if (!token || !apiBaseUrl) {
      clearStoredSessionToken();
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get<MeResponse>(`${apiBaseUrl}/auth/me`);
      setUser(response.data.user);
    } catch {
      clearStoredSessionToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post<LoginResponse>(`${apiBaseUrl}/auth/login`, {
      email,
      password,
    });

    setStoredSessionToken(response.data.token);
    setUser(response.data.user);
  }, []);

  const completeMicrosoftLogin = useCallback(async (token: string) => {
    if (!apiBaseUrl) {
      throw new Error('GCM API URL is not configured.');
    }

    setStoredSessionToken(token);

    try {
      const response = await axios.get<MeResponse>(`${apiBaseUrl}/auth/me`);
      setUser(response.data.user);
    } catch (error) {
      clearStoredSessionToken();
      setUser(null);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (getStoredSessionToken() && apiBaseUrl) {
        await axios.post(`${apiBaseUrl}/auth/logout`);
      }
    } finally {
      clearStoredSessionToken();
      setUser(null);
    }
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) {
      return false;
    }

    return user.permissions.includes('*') || user.permissions.includes(permission);
  }, [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isLoading,
    login,
    completeMicrosoftLogin,
    logout,
    hasPermission,
  }), [completeMicrosoftLogin, hasPermission, isLoading, login, logout, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};
